import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './Services.css';

const Services = () => {
  const mainServices = [
    {
      icon: 'fas fa-drafting-compass',
      title: 'Industrial Design',
      subtitle: 'Kitchen & Laundry Equipment',
      description: 'Complete design solutions for commercial kitchen and laundry facilities with focus on efficiency and functionality.',
      features: ['Custom Kitchen Layouts', 'Equipment Specification', 'Workflow Optimization', '3D Visualization'],
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      icon: 'fas fa-blueprint',
      title: 'MEP Executive Drawings',
      subtitle: 'Mechanical, Electrical & Plumbing',
      description: 'Comprehensive MEP design and documentation services ensuring compliance with all industry standards.',
      features: ['Detailed Technical Drawings', 'Load Calculations', 'System Integration', 'Code Compliance'],
      image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      icon: 'fas fa-chalkboard-teacher',
      title: 'Training Sessions',
      subtitle: 'Professional Operator Training',
      description: 'Comprehensive training programs for equipment operators ensuring safe and efficient operations.',
      features: ['Equipment Operation', 'Safety Protocols', 'Maintenance Basics', 'Certification Programs'],
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      icon: 'fas fa-tools',
      title: 'After Sale Service',
      subtitle: 'Complete Support Solutions',
      description: 'Comprehensive after-sales support including maintenance, repairs, and technical assistance.',
      features: ['24/7 Support', 'Preventive Maintenance', 'Emergency Repairs', 'Parts Supply'],
      image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    }
  ];

  const additionalServices = [
    {
      icon: 'fas fa-file-alt',
      title: 'Individual & General Specification',
      description: 'Detailed specifications for equipment and systems',
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
    },
    {
      icon: 'fas fa-chart-line',
      title: 'Load Consumption Schedule',
      description: 'Energy consumption analysis and optimization',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
    },
    {
      icon: 'fas fa-calendar-alt',
      title: 'Project Programme',
      description: 'Comprehensive project planning and scheduling',
      image: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
    },
    {
      icon: 'fas fa-fan',
      title: 'Fan Services',
      description: 'Desktop, Ceiling, Wall, Stand & Other Fan Repairing',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
    },
    {
      icon: 'fas fa-microchip',
      title: 'Kitchen Appliance Repair',
      description: 'Microwave, Flour Mixer, Blender, Iron, Gas Stove Repairing',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
    },
    {
      icon: 'fas fa-home',
      title: 'Home Appliance Services',
      description: 'Washing Machine, Fridge, AC, TV, Curtain Repair',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Consultation',
      description: 'Initial assessment and requirement analysis',
      icon: 'fas fa-comments'
    },
    {
      step: '02',
      title: 'Planning',
      description: 'Detailed project planning and design phase',
      icon: 'fas fa-clipboard-list'
    },
    {
      step: '03',
      title: 'Implementation',
      description: 'Professional installation and setup',
      icon: 'fas fa-cogs'
    },
    {
      step: '04',
      title: 'Support',
      description: 'Ongoing maintenance and support services',
      icon: 'fas fa-headset'
    }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="hero-background">
          <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Our Services" />
        </div>
        <div className="hero-overlay"></div>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="hero-content"
          >
            <h1>Our Services</h1>
            <p>Comprehensive facilities management solutions tailored to your specific needs</p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="main-services section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Core Services</h2>
            <p className="section-subtitle">
              Our primary service offerings designed to meet all your facility management needs
            </p>
          </motion.div>

          <div className="main-services-grid">
            {mainServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="main-service-card"
                whileHover={{ y: -10 }}
              >
                <div className="service-image">
                  <img src={service.image} alt={service.title} />
                  <div className="service-overlay">
                    <i className={service.icon}></i>
                  </div>
                </div>
                <div className="service-content">
                  <h3>{service.title}</h3>
                  <h4>{service.subtitle}</h4>
                  <p>{service.description}</p>
                  <ul className="service-features">
                    {service.features.map((feature, idx) => (
                      <li key={idx}>
                        <i className="fas fa-check"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to={`/services/${index === 0 ? 'industrial-design' : index === 1 ? 'mep-services' : index === 2 ? 'training' : 'after-sale'}`} className="service-btn">
                    Learn More <i className="fas fa-arrow-right"></i>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="additional-services section parallax-bg">
        <div className="parallax-overlay"></div>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Additional Services</h2>
            <p className="section-subtitle">
              Extended service portfolio to cover all aspects of facility management
            </p>
          </motion.div>

          <div className="additional-services-grid">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="additional-service-card"
                whileHover={{ y: -5 }}
              >
                <div className="service-image">
                  <img src={service.image} alt={service.title} />
                  <div className="service-icon">
                    <i className={service.icon}></i>
                  </div>
                </div>
                <div className="service-info">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Process</h2>
            <p className="section-subtitle">
              A systematic approach to delivering exceptional results
            </p>
          </motion.div>

          <div className="process-steps">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="process-step"
              >
                <div className="step-number">{step.step}</div>
                <div className="step-icon">
                  <i className={step.icon}></i>
                </div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="step-connector"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Gallery */}
      <section className="service-gallery section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Service Gallery</h2>
            <p className="section-subtitle">
              Visual showcase of our work and capabilities
            </p>
          </motion.div>

          <div className="gallery-grid">
            {[
              'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
              'https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
              'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
              'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
              'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
              'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
              'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
              'https://images.unsplash.com/photo-1611224923853-80b023f02d71?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'
            ].map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="gallery-item"
                whileHover={{ scale: 1.05 }}
              >
                <img src={image} alt={`Service ${index + 1}`} />
                <div className="gallery-overlay">
                  <i className="fas fa-search-plus"></i>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta parallax-bg">
        <div className="parallax-overlay"></div>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="cta-content"
          >
            <h2>Need Our Services?</h2>
            <p>Get in touch with our experts for a customized solution</p>
            <div className="cta-buttons">
              <a href="/contact" className="btn btn-primary">
                Get Quote
              </a>
              <a href="tel:+1234567890" className="btn btn-secondary">
                Call Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;